import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  LinearProgress,
} from '@mui/material';
import DrawIcon from '@mui/icons-material/Draw';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import {colors} from '../../../theme/colors';

interface Signer {
  email: string;
  name?: string;
  status: string;
  signedAt?: string;
}

interface EsignContext {
  signatureRequestId: string;
  signers: Signer[];
  allSigned?: boolean;
}

interface Props {
  esign: EsignContext;
}

const SIGNER_STATUS: Record<string, {icon: React.ReactNode; color: string}> = {
  signed: {icon: <CheckCircleIcon color="success" />, color: colors.green[600]},
  pending: {icon: <HourglassEmptyIcon color="warning" />, color: colors.amber[600]},
  declined: {icon: <CancelIcon color="error" />, color: colors.red[600]},
};

export const SignerChecklistPanel: React.FC<Props> = ({esign}) => {
  const signedCount = esign.signers.filter((s) => s.status === 'signed').length;
  const total = esign.signers.length;
  const progress = total > 0 ? (signedCount / total) * 100 : 0;

  return (
    <Paper variant="outlined" sx={{mx: 2, mb: 2, overflow: 'hidden'}}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: esign.allSigned ? colors.green[50] : colors.amber[50],
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <DrawIcon sx={{fontSize: 20, color: esign.allSigned ? colors.green[700] : colors.amber[700]}} />
        <Typography variant="subtitle2" sx={{fontWeight: 600, flex: 1}}>
          {esign.allSigned ? 'All signatures collected' : `Waiting for ${total - signedCount} of ${total} signatures`}
        </Typography>
        <Chip
          size="small"
          label={`${signedCount}/${total}`}
          color={esign.allSigned ? 'success' : 'warning'}
        />
      </Box>

      {!esign.allSigned && (
        <LinearProgress variant="determinate" value={progress} />
      )}

      <List dense disablePadding>
        {esign.signers.map((signer) => {
          const cfg = SIGNER_STATUS[signer.status] ?? SIGNER_STATUS.pending;
          return (
            <ListItem key={signer.email} divider>
              <ListItemIcon sx={{minWidth: 36}}>
                {cfg.icon}
              </ListItemIcon>
              <ListItemText
                primary={signer.name || signer.email}
                secondary={
                  signer.status === 'signed' && signer.signedAt
                    ? `Signed ${new Date(signer.signedAt).toLocaleString()}`
                    : signer.status === 'declined'
                    ? 'Declined'
                    : 'Pending'
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
};
